import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { StayCompliantNavItem } from "../../../../common/service/server/Types";
import { RouteHelpers, Routes } from "../../../root/routes";

export type NavItem = {
  item: StayCompliantNavItem;
  label: string;
  route?: string | ((organizationId: OrganizationDto["id"]) => string);
  hidden?: boolean | ((org: OrganizationDto) => boolean);
  isDisabled?: boolean;
  showIncompleteBadge?: boolean;
  itemAliases?: string[];
  children?: NavItem[];
  matchRouteOnPrefix?: boolean; // If true, will consider the nav item as active it the current page's route starts with the nav item's route
};

export const DIVIDER_LITERAL = "DIVIDER";
export type NavDivider = "DIVIDER";

const KNOWLEDGE_CENTER_NAV_ITEM: NavItem = {
  item: "KNOWLEDGE_CENTER",
  label: "Knowledge Center",
  route: Routes.knowledgeCenter.root,
};

const REQUEST_INBOX_NAV_ITEM: NavItem = {
  item: "REQUEST_INBOX",
  label: "Request Inbox",
  route: Routes.requests.active,
  itemAliases: ["/requests/"],
};

const HR_CENTER_NAV_ITEM: NavItem = {
  item: "HR_CENTER",
  label: "HR Center",
  route: Routes.hrCenter.root,
  children: [
    {
      item: "HR_CENTER_HR_DATA_MAP",
      label: "Data Map",
      route: Routes.hrCenter.hrDataMap.root,
      matchRouteOnPrefix: true,
    },
    {
      item: "HR_CENTER_EDIT_HR_RETENTION_POLICY",
      label: "Retention Policy",
      route: Routes.hrCenter.editHrRetentionPolicy,
    },
    {
      item: "HR_CENTER_HR_NOTICES",
      label: "Notices",
      route: Routes.hrCenter.hrNotices,
    },
  ],
};

export const NAV_ITEMS: (NavItem | NavDivider)[] = [
  {
    item: "DASHBOARD",
    label: "Dashboard",
    route: Routes.dashboard.root,
  },
  DIVIDER_LITERAL,
  REQUEST_INBOX_NAV_ITEM,
  {
    item: "WORKFLOWS",
    label: "DSAR Workflows",
    route: Routes.workflows.dashboard,
    children: [
      {
        item: "WORKFLOWS_API_INTEGRATIONS",
        label: "API Integrations",
        route: Routes.privacyCenter.integrations,
      },
      {
        item: "WORKFLOWS_DATA_STORAGE_SETTINGS",
        label: "Data Storage",
        route: Routes.workflows.dataStorageSettings,
      },
      {
        item: "WORKFLOWS_REQUESTS_WORKFLOWS",
        label: "Workflow Setup",
        route: Routes.organization.requestHandling.base,
        matchRouteOnPrefix: true,
      },
      {
        item: "WORKFLOWS_EMAIL_TEMPLATES",
        label: "Email Templates",
        route: Routes.workflows.emailTemplates,
      },
    ],
  },
  DIVIDER_LITERAL,
  { item: "TASK_LIST", label: "To-Do List", route: Routes.tasks.open },
  {
    item: "DATA_MAP_DASHBOARD",
    label: "Data Map",
    route: Routes.dataMap.dashboard,
    children: [
      {
        item: "BUSINESS_SURVEY",
        label: "Business Profile",
        route: Routes.organization.settings.businessSurvey,
      },
      {
        item: "DATA_MAP",
        label: "Data Map",
        route: Routes.dataMap.root,
      },
      {
        item: "VENDOR_DOCUMENTATION",
        label: "Vendor Documents",
        route: Routes.dataMap.vendorDocumentation,
      },
      {
        item: "DATA_RETENTION",
        label: "Data Retention",
        route: Routes.dataMap.dataRetention,
      },
    ],
  },
  DIVIDER_LITERAL,
  {
    item: "PRIVACY_CENTER",
    label: "Privacy Center",
    route: Routes.privacyCenter.root,
    children: [
      {
        item: "PRIVACY_CENTER_POLICIES_AND_NOTICES",
        label: "Policies & Notices",
        route: Routes.privacyCenter.privacyPolicy,
        itemAliases: [Routes.privacyCenter.usStates, Routes.privacyCenter.theme],
      },
      {
        item: "PRIVACY_CENTER_DSAR_FORMS",
        label: "DSAR Forms",
        route: Routes.privacyCenter.dsarForms,
      },
      {
        item: "PRIVACY_CENTER_DEVELOPER_INSTRUCTIONS",
        label: "Developer Instructions",
        route: Routes.privacyCenter.publish,
      },
      {
        item: "PRIVACY_CENTER_WEBSITE_CONSENT",
        label: "Website Consent",
        route: Routes.privacyCenter.consentBanner,
      },
    ],
  },
  DIVIDER_LITERAL,
  HR_CENTER_NAV_ITEM,
  DIVIDER_LITERAL,
  { item: "DOCUMENTS", label: "Privacy Docs", route: Routes.documents.root },
  KNOWLEDGE_CENTER_NAV_ITEM,
  DIVIDER_LITERAL,
  {
    item: "EVENT_PULSE",
    label: "SignalOps",
    route: (organizationId) => RouteHelpers.organization.eventPulse(organizationId),
    hidden: (org) => org.featureEventPulse !== true,
  },
];

export const NAV_ITEMS_GENERAL_ONLY = NAV_ITEMS.filter(
  (item) =>
    item == DIVIDER_LITERAL ||
    ((item as NavItem).item !== "DOCUMENTS" &&
      // See POL-6912 for context to not allow General users to access HR Center
      (item as NavItem).item !== "HR_CENTER"),
);

export const NAV_ITEMS_HR_ONLY: (NavItem | NavDivider)[] = [
  REQUEST_INBOX_NAV_ITEM,
  DIVIDER_LITERAL,
  KNOWLEDGE_CENTER_NAV_ITEM,
  HR_CENTER_NAV_ITEM,
];
